import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const formatMoney = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatPercent = (value) =>
  `${Number(value || 0).toLocaleString('pt-BR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;

const CollectionsMetricsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadMetrics = async () => {
    setLoading(true);
    try {
      const { data: payload } = await axios.get('/api/collections/metrics');
      setData(payload || null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar metricas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, []);

  const cards = useMemo(() => {
    return [
      {
        label: 'Taxa de recuperacao',
        value: formatPercent(data?.taxa_recuperacao_percentual || 0),
        hint: 'Valor recuperado / carteira total acompanhada',
        tone: 'border-emerald-200 bg-emerald-50 text-emerald-900',
      },
      {
        label: 'Tempo medio para receber',
        value: `${Number(data?.tempo_medio_recuperacao_dias || 0).toFixed(1)} dias`,
        hint: 'Media dos casos recuperados',
        tone: 'border-sky-200 bg-sky-50 text-sky-900',
      },
      {
        label: 'Recuperacao ate D+30',
        value: formatPercent(data?.recuperacao_ate_d30_percentual || 0),
        hint: `${data?.recuperadas_ate_d30 || 0} de ${data?.total_recuperadas_fechadas || 0} recuperadas`,
        tone: 'border-amber-200 bg-amber-50 text-amber-900',
      },
      {
        label: 'Carteira em risco',
        value: formatMoney(data?.carteira_aberta || 0),
        hint: `${data?.casos_em_recuperacao || 0} casos em acompanhamento`,
        tone: 'border-rose-200 bg-rose-50 text-rose-900',
      },
    ];
  }, [data]);

  return (
    <div className="animate-fade-in space-y-6">
      <section className="rounded-2xl border border-emerald-300 bg-gradient-to-r from-slate-900 via-emerald-900 to-cyan-900 p-6 text-white shadow-soft">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold">Metricas de Recuperacao</h1>
            <p className="mt-2 text-emerald-100">Indicadores de performance da cobranca inteligente para orientar decisoes de caixa.</p>
          </div>
          <button className="btn-primary" onClick={loadMetrics} disabled={loading}>
            {loading ? 'Atualizando...' : 'Atualizar'}
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => (
          <div key={card.label} className={`rounded-xl border p-4 shadow-sm ${card.tone}`}>
            <p className="text-xs uppercase tracking-wide opacity-80">{card.label}</p>
            <p className="mt-1 text-2xl font-semibold">{card.value}</p>
            <p className="mt-1 text-xs opacity-80">{card.hint}</p>
          </div>
        ))}
      </section>

      <div className="card">
        <div className="card-header">Efetividade por canal</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Canal</th>
                  <th>Acoes totais</th>
                  <th>Acoes efetivas</th>
                  <th>Efetividade</th>
                </tr>
              </thead>
              <tbody>
                {(data?.canais || []).map((channel) => {
                  const total = Number(channel.total_acoes || 0);
                  const effective = Number(channel.acoes_efetivas || 0);
                  const rate = total > 0 ? (effective / total) * 100 : 0;
                  return (
                    <tr key={channel.canal}>
                      <td className="font-medium">{channel.canal}</td>
                      <td>{total}</td>
                      <td>{effective}</td>
                      <td>{formatPercent(rate)}</td>
                    </tr>
                  );
                })}
                {(data?.canais || []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="text-center py-10 text-gray-500">
                      Nenhuma acao registrada ainda
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="card">
          <div className="card-header">Valor recuperado por regua</div>
          <div className="card-body p-0">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Regua</th>
                    <th>Casos recuperados</th>
                    <th>Valor recuperado</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.recuperacao_por_regua || []).map((row) => (
                    <tr key={row.policy_name}>
                      <td className="font-medium">{row.policy_name}</td>
                      <td>{Number(row.casos_recuperados || 0)}</td>
                      <td>{formatMoney(row.valor_recuperado || 0)}</td>
                    </tr>
                  ))}
                  {(data?.recuperacao_por_regua || []).length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center py-10 text-gray-500">Sem dados de recuperacao por regua</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header">Valor recuperado por canal</div>
          <div className="card-body p-0">
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Canal</th>
                    <th>Casos recuperados</th>
                    <th>Valor recuperado</th>
                  </tr>
                </thead>
                <tbody>
                  {(data?.valor_recuperado_por_canal || []).map((row) => (
                    <tr key={row.canal}>
                      <td className="font-medium">{row.canal}</td>
                      <td>{Number(row.casos_recuperados || 0)}</td>
                      <td>{formatMoney(row.valor_recuperado || 0)}</td>
                    </tr>
                  ))}
                  {(data?.valor_recuperado_por_canal || []).length === 0 && (
                    <tr>
                      <td colSpan={3} className="text-center py-10 text-gray-500">Sem dados de recuperacao por canal</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Efetividade por template</div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Template</th>
                  <th>Canal</th>
                  <th>Acoes totais</th>
                  <th>Acoes efetivas</th>
                  <th>Efetividade</th>
                </tr>
              </thead>
              <tbody>
                {(data?.efetividade_por_template || []).map((row) => {
                  const total = Number(row.total_acoes || 0);
                  const effective = Number(row.acoes_efetivas || 0);
                  const rate = total > 0 ? (effective / total) * 100 : 0;
                  return (
                    <tr key={`${row.template_name}-${row.canal}`}>
                      <td className="font-medium">{row.template_name}</td>
                      <td>{row.canal}</td>
                      <td>{total}</td>
                      <td>{effective}</td>
                      <td>{formatPercent(rate)}</td>
                    </tr>
                  );
                })}
                {(data?.efetividade_por_template || []).length === 0 && (
                  <tr>
                    <td colSpan={5} className="text-center py-10 text-gray-500">Nenhum template com disparo registrado</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CollectionsMetricsPage;
